/**
 * SupplierInquiryAktionenTab — config wrapper for the shared ExporterAktionenTab.
 *
 * Mirrors CustomerConformityAktionenTab but anchored on supplier scope.
 * The AP sends an SVHC questionnaire to a supplier; the supplier's reply is
 * fed back into the existing supplier importer AP (Lieferanten Deklarationen)
 * via the email-inbox / pending-document flow.
 */

import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useMemo } from "react"
import { api } from "../../../api/client"
import {
  renderLieferantenAbfrage,
  type LieferantenAbfrageHtmlData,
} from "../../../lib/templates/html/lieferanten_abfrage"
import { generateLieferantenAbfragePdf } from "../../../lib/templates/pdf/lieferanten_abfrage_pdf"
import type { ScopeDiffState } from "../../../types"
import type {
  ExporterFreshnessData,
  ExporterTabConfig,
} from "../shared/ExporterAktionenTab"
import { ExporterAktionenTab } from "../shared/ExporterAktionenTab"
import { seedExportDiff } from "../shared/seedExportDiff"

function todayFormatted(): string {
  return new Date().toLocaleDateString("de-DE")
}

function dueDateFormatted(daysFromNow: number): string {
  const d = new Date()
  d.setDate(d.getDate() + daysFromNow)
  return d.toLocaleDateString("de-DE")
}

function computeOverrides(
  diffs: Record<string, ScopeDiffState> | undefined,
): Record<string, Record<string, string>> | undefined {
  if (!diffs) return undefined
  const result: Record<string, Record<string, string>> = {}
  for (const sd of Object.values(diffs)) {
    for (const recDiff of Object.values(sd.records)) {
      for (const cell of Object.values(recDiff.cells)) {
        if (
          cell.fileValue !== null &&
          cell.fileValue !== undefined &&
          cell.fileValue !== cell.dbValue
        ) {
          result[recDiff.recordId] ??= {}
          result[recDiff.recordId][cell.primitiveName] = cell.fileValue
        }
      }
    }
  }
  return Object.keys(result).length > 0 ? result : undefined
}

function buildHtmlData(
  c: Awaited<ReturnType<typeof api.supplierInquiryData>>[number],
): LieferantenAbfrageHtmlData {
  const today = todayFormatted()
  return {
    issuer: "",
    issuerAddress: "",
    creationDate: today,
    signatory: "",
    signatoryRole: "",
    signatoryPlace: "",
    signatoryDate: today,
    responseDueDate: dueDateFormatted(14),
    supplier: {
      supplierId: c.supplier_id,
      supplierName: c.supplier_name,
      contactName: c.contact_name,
      contactPosition: c.contact_position,
      contactLocation: c.contact_location,
      totalComponentCount: c.total_component_count,
      componentsWithSvhcCount: c.components_with_svhc_count,
      components: c.components.map((comp) => ({
        componentId: comp.component_id,
        componentName: comp.component_name,
        lastDeclaredSvhcPresent: comp.last_declared_svhc_present,
        lastSupplierInfoDate: comp.last_supplier_info_date,
        rows: comp.rows.map((r) => ({
          componentId: r.component_id,
          componentName: r.component_name,
          casNumber: r.cas_number,
          substanceName: r.substance_name,
          lastDeclaredSvhcGehalt: r.last_declared_svhc_gehalt,
        })),
      })),
    },
  }
}

async function renderInquiryHtml(
  recordId: string,
  diffs: Record<string, ScopeDiffState> | undefined,
): Promise<string | null> {
  const overrides = computeOverrides(diffs)
  const data = await api.supplierInquiryData([recordId], overrides)
  const c = data[0]
  if (!c) return null
  return renderLieferantenAbfrage(buildHtmlData(c))
}

async function doInquiryExport(
  recordId: string,
  diffs: Record<string, ScopeDiffState> | undefined,
  noDataMessage: string,
): Promise<void> {
  const overrides = computeOverrides(diffs)
  const data = await api.supplierInquiryData([recordId], overrides)
  const c = data[0]
  if (!c) throw new Error(noDataMessage)
  generateLieferantenAbfragePdf(buildHtmlData(c))
}

export function SupplierInquiryAktionenTab() {
  const { translate } = useTranslation()

  const config = useMemo<ExporterTabConfig>(
    () => ({
      apId: "lieferanten-abfragen",
      heading: translate(
        "QMS/AP/SUPPLIER_INQUIRY/NAME" as TranslationKeys,
      ),
      subheading: translate(
        "QMS/AP/SUPPLIER_INQUIRY/SUBHEADING" as TranslationKeys,
      ),
      scopeId: "supplier",
      recordLabel: translate(
        "QMS/AP/SUPPLIER_INQUIRY/RECORD_LABEL" as TranslationKeys,
      ),

      // Cross-scope chain-walk search (overrides the shell's local default).
      search: (q) =>
        api.supplierSearch(q).then((results) =>
          results.map((r) => ({
            record_id: r.supplier_id,
            display_name: r.supplier_name,
            matches: r.matches.map((m) => ({
              scope_id: m.scope_id,
              primitive_title: m.primitive_title,
              value: m.value,
            })),
            extra_match_count: Math.max(0, r.matches.length - 1),
          })),
        ),

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fetchFreshness: (threshold, _records): Promise<ExporterFreshnessData> =>
        api.supplierFreshnessPartition(threshold),

      renderPreviewHtml: renderInquiryHtml,
      doExport: (recordId, diffs) =>
        doInquiryExport(
          recordId,
          diffs,
          translate("QMS/COMMON/NO_DATA" as TranslationKeys),
        ),
      onStartExport: (recordId) =>
        seedExportDiff(recordId, ["supplier", "component", "substance"]),

      cockpitTemplates: [
        {
          id: "lieferanten-abfrage",
          title: translate(
            "QMS/AP/SUPPLIER_INQUIRY/TEMPLATE_TITLE" as TranslationKeys,
          ),
        },
      ],
      defaultFollowUpDays: 14,
      apOwnEmail: "lieferanten-abfragen@ap.turnus.ai",
      subjectTemplateDefault: translate(
        "QMS/AP/SUPPLIER_INQUIRY/EMAIL_SUBJECT_DEFAULT" as TranslationKeys,
      ),
      bodyTemplateDefault: translate(
        "QMS/AP/SUPPLIER_INQUIRY/EMAIL_BODY_DEFAULT" as TranslationKeys,
      ),
    }),
    [translate],
  )

  return <ExporterAktionenTab config={config} />
}
